import {
  Listing,
  OpenHouse,
  MessageTopicKey,
  ClientEventName,
  ClientEventSource
} from '../../../../api/gqlaxy/gql/generated/graphql'
import { useSelector } from 'react-redux'
import { format, formatISO } from 'date-fns'
import { useIntl, defineMessages } from 'react-intl'
import { useGetClientId } from '../../../../hooks/useGetClientId'
import { selectClientFirstName } from '../../../../store/selectors/client'
import { useCreateClientEvent } from '../../../../hooks/useCreateClientEvent'
import { useSendListingMessage } from '../../../../hooks/listings/useSendListingMessage'
import { Button, Icon, Link, Menu, MenuButton, MenuItem, MenuList } from '@homebotapp/hb-react-component-catalog'

const YAHOO_CALENDAR_URL = 'https://calendar.yahoo.com/?v=60'
const OUTLOOK_CALENDAR_URL = 'https://outlook.live.com/calendar/0/action/compose?'
const GOOGLE_CALENDAR_URL = 'https://calendar.google.com/calendar/render?action=TEMPLATE'

export const MSG = defineMessages({
  addToCalendar: {
    id: 'ListingDetails.ScheduleTour.addToCalendar',
    defaultMessage: 'Add to calendar'
  },
  google: {
    id: 'ListingDetails.ScheduleTour.google',
    defaultMessage: 'Google'
  },
  yahoo: {
    id: 'ListingDetails.ScheduleTour.yahoo',
    defaultMessage: 'Yahoo'
  },
  outlook: {
    id: 'ListingDetails.ScheduleTour.outlook',
    defaultMessage: 'Outlook'
  },
  iCal: {
    id: 'ListingDetails.ScheduleTour.iCal',
    defaultMessage: 'iCal'
  },
  openHouseTitle: {
    id: 'ListingDetails.ScheduleTour.openHouseTitle',
    defaultMessage: 'Open House for {addr}'
  }
})

export interface OpenHouseAddToCalendarButtonProps {
  listing?: Listing
  openHouse: OpenHouse
}

export const OpenHouseAddToCalendarButton = ({ listing, openHouse }: OpenHouseAddToCalendarButtonProps) => {
  const intl = useIntl()
  const clientId = useGetClientId()
  const clientFirstName = useSelector(selectClientFirstName)
  const { mutateAsync: sendListingMessage } = useSendListingMessage()
  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  const fireOffClientEvent = async () => {
    await sendListingMessage({
      input: {
        clientId: clientId,
        listingId: listing.id,
        openHouseId: openHouse.id,
        topicKey: MessageTopicKey.ListingsOpenHouseRequest,
        message: `${clientFirstName} added an open house to their calendar on ${format(
          new Date(openHouse.startTime?.replace(' ', 'T')),
          'E M/d'
        )} from ${format(new Date(openHouse.startTime?.replace(' ', 'T')), 'h:mm a')} - ${format(
          new Date(openHouse.endTime?.replace(' ', 'T')),
          'h:mm a'
        )}`
      }
    })

    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.OpenHouseAddedToCalendar,
        eventSource: ClientEventSource.ListingDetailed,
        listingId: listing.id,
        openHouseId: openHouse.id
      }
    })
  }

  const getGoogleCalenderEventURL = () => {
    return `${GOOGLE_CALENDAR_URL}&text=${intl.formatMessage(MSG.openHouseTitle, {
      addr: `${listing.address.number} ${listing.address.street}`
    })}&details=${openHouse.remarks}&dates=${formatISO(new Date(openHouse.startTime), { format: 'basic' })}/${formatISO(
      new Date(openHouse.endTime),
      { format: 'basic' }
    )}&location=${listing.address.number} ${listing.address.street} ${listing.address.city}, ${
      listing.address.stateOrProvince
    }`
  }

  const getYahooCalendarEventURL = () => {
    return `${YAHOO_CALENDAR_URL}&title=${intl.formatMessage(MSG.openHouseTitle, {
      addr: `${listing.address.number} ${listing.address.street}`
    })}&desc=${openHouse.remarks}&st=${formatISO(new Date(openHouse.startTime), { format: 'basic' })}&et=${formatISO(
      new Date(openHouse.endTime),
      { format: 'basic' }
    )}&in_loc=${listing.address.number} ${listing.address.street} ${listing.address.city}, ${
      listing.address.stateOrProvince
    } ${listing.address.postalCode}`
  }

  const getOutlookCalendarEventURL = () => {
    return `${OUTLOOK_CALENDAR_URL}&subject=${intl.formatMessage(MSG.openHouseTitle, {
      addr: `${listing.address.number} ${listing.address.street}`
    })}&body=${openHouse.remarks}&startdt=${formatISO(new Date(openHouse.startTime)).substring(
      0,
      formatISO(new Date(openHouse.startTime)).lastIndexOf('-')
    )}&enddt=${formatISO(new Date(openHouse.endTime)).substring(
      0,
      formatISO(new Date(openHouse.endTime)).lastIndexOf('-')
    )}&location=${listing.address.number} ${listing.address.street} ${listing.address.city}, ${
      listing.address.stateOrProvince
    }`
  }

  const getIcsFile = () => {
    // NOTE: I know the formatting/indentation is a bit weird here, but it's to make the ICS file valid
    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Homebot//EN
BEGIN:VEVENT
UID:${openHouse.id}
URL:${window.location.href}
DTSTAMP:${formatISO(new Date(), { format: 'basic' }).split('-')[0]}
DTSTART:${formatISO(new Date(openHouse.startTime), { format: 'basic' }).split('-')[0]}
DTEND:${formatISO(new Date(openHouse.endTime), { format: 'basic' }).split('-')[0]}
SUMMARY:${intl.formatMessage(MSG.openHouseTitle, {
      addr: `${listing.address.number} ${listing.address.street}`
    })}
DESCRIPTION:${openHouse.remarks}
LOCATION:${listing.address.number} ${listing.address.street} ${listing.address.city}, ${listing.address.stateOrProvince}
END:VEVENT
END:VCALENDAR`
  }

  return (
    <Menu>
      <MenuButton
        mt={2}
        size='xs'
        as={Button}
        leftIcon={<Icon name='calendar' />}
        tracking={{
          descriptor: 'Add to calendar',
          guid: 'kV2gpY6rWv6HRa6TUvnjbs',
          ui_context: 'Listing Details Page Open House Info'
        }}
      >
        {intl.formatMessage(MSG.addToCalendar)}
      </MenuButton>
      <MenuList>
        <MenuItem
          as={Link}
          onClick={fireOffClientEvent}
          href={getGoogleCalenderEventURL()}
          target='_blank'
          tracking={{
            guid: 'uSbTEZhhwquG6PtDMXiEds',
            ui_context: 'Listing Details Page Open House Info',
            descriptor: 'Add open house to google calendar'
          }}
        >
          {intl.formatMessage(MSG.google)}
        </MenuItem>
        <MenuItem
          as={Link}
          download='open-house.ics'
          onClick={fireOffClientEvent}
          href={`data:text/calendar;charset=utf8,${encodeURIComponent(getIcsFile())}`}
          tracking={{
            guid: 'kguY6GD9Z8qcvbBz3ZwKv1',
            ui_context: 'Listing Details Page Open House Info',
            descriptor: 'Add open house to calendar ICS file'
          }}
        >
          {intl.formatMessage(MSG.iCal)}
        </MenuItem>
        <MenuItem
          as={Link}
          onClick={fireOffClientEvent}
          href={getOutlookCalendarEventURL()}
          target='_blank'
          tracking={{
            guid: '9MHVHHMNGPhboP42XCq9XR',
            ui_context: 'Listing Details Page Open House Info',
            descriptor: 'Add open house to outlook calendar'
          }}
        >
          {intl.formatMessage(MSG.outlook)}
        </MenuItem>
        <MenuItem
          as={Link}
          onClick={fireOffClientEvent}
          href={getYahooCalendarEventURL()}
          target='_blank'
          tracking={{
            guid: '1gmcSx7STeJA2KKMEwPUQw',
            ui_context: 'Listing Details Page Open House Info',
            descriptor: 'Add open house to yahoo calendar'
          }}
        >
          {intl.formatMessage(MSG.yahoo)}
        </MenuItem>
      </MenuList>
    </Menu>
  )
}
